'use client'

import { useEffect, useRef, useState } from 'react'

import { faBookmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/navigation'
import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  Button,
  FormattedMessage,
  Modal,
} from '@b-stock/fe-core/bstock-react'
import { Colors } from '@b-stock/fe-core/design-system'
import type { SavedSearchDto } from '@b-stock/saved-search-api-client'
import type { AllListingsFilters } from '@b-stock/search-api-client'

import { ModalContent, ModalFooter } from '@/components/common/modal/ui'
import SavedSearchProvider, {
  useSavedSearch,
} from '@/components/listings/search/savedSearch/SavedSearchContext'
import SavedSearchItem from '@/components/listings/search/savedSearch/SavedSearchItem'
import { SearchList } from '@/components/listings/search/savedSearch/ui'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

const StyledIcon = styled(FontAwesomeIcon)`
  font-size: 1.25rem;
  color: ${Colors.Semantic.BStock.default.lightBackground};
  padding-right: 0.75rem;
`

type HomeSavedSearchModalProps = {
  closeModal: () => void
  isMobile?: boolean
  onSearchesUpdate?: (searches: SavedSearchDto[]) => void
}

// Minimal allFilters for SavedSearchProvider (only used for filter formatting, not needed for modal)
const minimalAllFilters: AllListingsFilters = {
  categories: [],
  storefront: [],
  condition: [],
  transportMode: [],
  inventoryType: [],
  listingType: [],
  buyingFormat: [],
  region: [],
}

const HomeSavedSearchModalContent = ({
  closeModal,
  isMobile = false,
}: {
  closeModal: () => void
  isMobile?: boolean
}) => {
  const { trackButtonClicked } = useAnalytics()
  const router = useRouter()
  const { savedSearches } = useSavedSearch()
  const savedSearchTitleId = 'yourShoppingLists'

  const [submitDisabled, setSubmitDisabled] = useState(true)
  const [selected, setSelected] = useState<SavedSearchDto['_id'] | null>(null)

  // Track previous length to detect deletions
  const prevLengthRef = useRef<number>(savedSearches.length)

  useEffect(() => {
    setSubmitDisabled(selected === null) // oxlint-disable-line react/set-state-in-effect
  }, [selected])

  // Auto-close modal when all searches are deleted (transition from >0 to 0)
  useEffect(() => {
    const prevLength = prevLengthRef.current
    const currentLength = savedSearches.length

    // Auto-close only when transitioning from >0 to 0 (deletion occurred)
    if (prevLength > 0 && currentLength === 0) {
      closeModal()
    }

    // Update ref for next render
    prevLengthRef.current = currentLength
  }, [savedSearches.length, closeModal])

  const handleCloseModal = () => {
    trackButtonClicked({
      screenName: 'homepage',
      buttonName: 'exit_saved_search_modal',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    })
    closeModal()
  }

  const handleCancel = () => {
    handleCloseModal()
  }

  const applySelected = () => {
    trackButtonClicked({
      screenName: 'home',
      buttonName: 'save_search_click',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      saved_search_id: selected,
    })
    if (!selected) return
    router.push(`/all-auctions?savedSearchId=${selected}`)
    closeModal()
  }

  return (
    <Modal closeModal={handleCloseModal}>
      <Modal.Header>
        <StyledIcon icon={faBookmark} />
        <FormattedMessage id={`SavedSearch.${savedSearchTitleId}`} />
      </Modal.Header>

      <ModalContent $size="tall">
        <SearchList>
          {savedSearches.map((ss) => (
            <SavedSearchItem
              key={ss._id}
              {...ss}
              selected={selected === ss._id}
              setSelected={setSelected}
              isHomePage
            />
          ))}
        </SearchList>
      </ModalContent>

      <ModalFooter>
        <Modal.Actions alignment={isMobile ? 'firstOnLeft' : 'end'}>
          <Button
            type="button"
            onClick={handleCancel}
            size="medium"
            appearance="tertiary"
          >
            <FormattedMessage id="SavedSearch.modal.btn.close" />
          </Button>

          <Button
            type="button"
            onClick={applySelected}
            size="medium"
            disabled={submitDisabled}
          >
            <FormattedMessage id="SavedSearch.modal.btn.search" />
          </Button>
        </Modal.Actions>
      </ModalFooter>
    </Modal>
  )
}

const HomeSavedSearchModal = ({
  closeModal,
  isMobile = false,
}: HomeSavedSearchModalProps) => {
  return (
    <SavedSearchProvider allFilters={minimalAllFilters}>
      <HomeSavedSearchModalContent
        closeModal={closeModal}
        isMobile={isMobile}
      />
    </SavedSearchProvider>
  )
}

export default HomeSavedSearchModal
